import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../lib/api';
import { vehicleName } from '../lib/utils';
import { MesRetenuesSav } from '../components/MesRetenuesSav';
import { Plus, TrendingUp, Car, ChevronRight, Loader2 } from 'lucide-react';

function fmt(p) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(p); }
function calcMargin(v) {
    const vr = v.prix_vente + v.frais_circulation - v.remise_client;
    const mb = vr - v.prix_achat;
    const tva = mb > 0 ? mb * 20 / 120 : 0;
    let fHT = 0;
    (v.frais || []).forEach(f => { fHT += f.tva_recuperable ? f.montant_ttc / 1.20 : f.montant_ttc; });
    return mb - tva - fHT;
}

export default function MobileDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState([]);
    const [commission, setCommission] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            const now = new Date();
            const mois = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const [vRes, cRes] = await Promise.all([
                axios.get(`${API_URL}/api/vehicles`, { withCredentials: true }),
                axios.get(`${API_URL}/api/commissions?mois=${mois}`, { withCredentials: true })
            ]);
            setVehicles(vRes.data);
            if (cRes.data.length > 0) setCommission(cRes.data[0]);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const currentMonth = useMemo(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }, []);

    const monthVehicles = useMemo(() =>
        vehicles.filter(v => v.date_vente && v.date_vente.startsWith(currentMonth))
    , [vehicles, currentMonth]);

    const totalMarge = useMemo(() =>
        monthVehicles.reduce((s, v) => s + calcMargin(v), 0)
    , [monthVehicles]);

    const recentVehicles = vehicles.slice(0, 5);

    const firstName = (user?.name || '').split(' ')[0] || user?.name || '';

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
    );

    return (
        <div className="px-5 pt-[calc(1rem+env(safe-area-inset-top))] pb-6" data-testid="mobile-dashboard">
            {/* Greeting */}
            <div className="mb-6 animate-fade-in">
                <h1 className="text-2xl font-bold text-gray-900">Bonjour {firstName}</h1>
                <p className="text-sm text-gray-400 mt-0.5">Voici votre activité du mois</p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 gap-3 mb-6 animate-fade-in stagger-1">
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="mobile-kpi-commission">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 font-medium">Commission estimée</p>
                    <p className="text-xl font-bold font-mono text-emerald-600">{fmt(commission?.commission || 0)}</p>
                </div>
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="mobile-kpi-marge">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 font-medium">Marge réalisée</p>
                    <p className="text-xl font-bold font-mono text-gray-900">{fmt(totalMarge)}</p>
                </div>
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="mobile-kpi-ventes">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 font-medium">Ventes du mois</p>
                    <p className="text-xl font-bold font-mono text-gray-900">{monthVehicles.length}</p>
                </div>
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100" data-testid="mobile-kpi-progress">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wider mb-1 font-medium">Progression</p>
                    {commission?.seuil > 0 ? (
                        <>
                            <p className="text-xl font-bold font-mono text-gray-900">{commission.progress_pct?.toFixed(0) || 0}%</p>
                            <div className="w-full h-1.5 rounded-full bg-gray-100 mt-2 overflow-hidden">
                                <div className={`h-full rounded-full transition-all duration-700 ${commission.progress_pct >= 100 ? 'bg-emerald-500' : 'bg-[#6366F1]'}`}
                                     style={{ width: `${Math.min(100, commission?.progress_pct || 0)}%` }} />
                            </div>
                        </>
                    ) : (
                        <p className="text-xl font-bold font-mono text-gray-300">—</p>
                    )}
                </div>
            </div>

            {/* CTA Nouveau dossier */}
            <button
                onClick={() => navigate('/calculator')}
                className="w-full bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white rounded-2xl p-4 flex items-center justify-center gap-2.5 shadow-lg shadow-indigo-200 transition-all mb-8 animate-fade-in stagger-2"
                data-testid="mobile-new-btn"
            >
                <Plus className="w-5 h-5" strokeWidth={2.5} />
                <span className="text-[15px] font-semibold">Nouveau dossier</span>
            </button>

            {/* Recent vehicles */}
            <div className="animate-fade-in stagger-3">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Mes derniers dossiers</h2>
                {recentVehicles.length === 0 ? (
                    <div className="text-center py-10">
                        <Car className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm text-gray-400">Aucun dossier</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {recentVehicles.map(v => {
                            const m = calcMargin(v);
                            const statut = v.date_vente ? 'Vendu' : 'En stock';
                            const statutColor = v.date_vente ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700';
                            return (
                                <button
                                    key={v.id}
                                    onClick={() => navigate(`/calculator/${v.id}`)}
                                    className="w-full bg-white rounded-xl p-3.5 flex items-center gap-3 shadow-sm border border-gray-100 active:scale-[0.98] transition-transform text-left"
                                    data-testid={`mobile-vehicle-${v.id}`}
                                >
                                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                                        <Car className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[13px] font-medium text-gray-900 truncate">{vehicleName(v)}</p>
                                        {v.immatriculation && <p className="text-[11px] text-gray-400 font-mono">{v.immatriculation}</p>}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={`text-[13px] font-mono font-semibold ${m >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(m)}</p>
                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${statutColor}`}>{statut}</span>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            {user?.role === 'user' && <div className="mt-6"><MesRetenuesSav /></div>}
        </div>
    );
}
